/**
 * AlertsFlagsPanel — the "Alerts & Flags" feed shared by the HOD
 * Dashboard (agent-subject alerts) and the CEO Dashboard (HOD-subject
 * alerts). Each alert row carries inline Feedback / Warning composers
 * that message the flagged person directly.
 *
 * Extracted from HODDashboard so both dashboards render the exact same
 * panel — the HOD page scopes it to agents, the CEO page to HODs, by
 * filtering on the `subject_class` field the backend stamps on every
 * alert.
 */

import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { AlertTriangle, Clock, CheckCircle2, MessageSquare, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline Feedback / Warning composer attached to each alert row. Sends a
// direct message to the flagged person (agent or HOD) via the existing
// /api/hod/agents/{id}/direct-message endpoint.
const AlertAgentActions = ({ alert, token, isDarkMode }) => {
  const [mode, setMode] = useState(null); // null | 'feedback' | 'warning'
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  // Some alert types reference the person in `agent_id`, others in
  // `assigned_to` (task owner / deal owner). Either works for the
  // direct-message endpoint.
  const targetId = alert.agent_id || alert.assigned_to;
  if (!targetId) return null;

  // Prefer the explicit subject_name the backend stamps; fall back to
  // parsing the alert title ("No field visit update: Maria Garcia").
  const personName = alert.subject_name || (() => {
    const t = alert.title || '';
    const idx = t.lastIndexOf(': ');
    return idx >= 0 ? t.slice(idx + 2).trim() : 'there';
  })();
  // Label the recipient by class so a CEO sees "Your HOD" framing is
  // wrong — sign-off adapts to who is being messaged.
  const recipientWord = alert.subject_class === 'hod' ? 'HOD' : 'agent';

  // Context-aware prefill per alert type.
  const composeText = (m) => {
    const isWarn = m === 'warning';
    const greet = `Hi ${personName.split(' ')[0]}`;
    const sign = '— Management';

    if (alert.type === 'no_status_update') {
      if (isWarn) {
        return `WARNING: Field activity not logged today\n\n` +
          `${greet}, your status hasn't been updated all day and no field activity or visits are on record. This is a formal warning — log your current status and any work you've done so far within the next hour and reply here with an explanation. Continued silence will be escalated.\n\n${sign}`;
      }
      return `${greet},\n\nI noticed you haven't logged any field activity or visits yet today. Quick check — everything OK? Drop your current status in the app and a one-line update on what you're working on. If you're blocked on anything, tell me here and I'll unblock it.\n\n${sign}`;
    }

    if (alert.type === 'ooo_no_return') {
      if (isWarn) {
        return `WARNING: OOO without an expected return time\n\n` +
          `${greet}, you marked yourself out of office but didn't set an expected return time. Please update that immediately so the team knows when to expect you back, and reply with the reason.\n\n${sign}`;
      }
      return `${greet},\n\nYou're showing as OOO without an expected return time. Please add an expected return so the team can plan coverage, and let me know if anything's going on that I should be aware of.\n\n${sign}`;
    }

    if (alert.type === 'stuck_deal') {
      if (isWarn) {
        return `WARNING: Deal stuck in negotiation\n\n` +
          `${greet}, ${(alert.title || '').replace('Deal stuck in negotiation: ', '')} hasn't moved in two weeks. This is a formal warning — either close it, re-qualify it, or kill it. Reply within today with your concrete next step and a date.\n\n${sign}`;
      }
      return `${greet},\n\nThe deal "${(alert.title || '').replace('Deal stuck in negotiation: ', '')}" has been sitting in negotiation for a while. What's blocking it? If you need me to step in, tell me here and I'll move on it today.\n\n${sign}`;
    }

    if (alert.type === 'overdue_followup') {
      if (isWarn) {
        return `WARNING: Overdue follow-up\n\n` +
          `${greet}, your follow-up "${(alert.title || '').replace('Overdue: ', '')}" is past due. This is a formal warning — close it or update its due date with a justified reason today, and reply here with the status.\n\n${sign}`;
      }
      return `${greet},\n\nYour follow-up "${(alert.title || '').replace('Overdue: ', '')}" slipped past its due date. Can you close it out today, or if it genuinely needs more time, reply here with what you need and when it'll be done?\n\n${sign}`;
    }

    if (isWarn) {
      return `WARNING: ${alert.title}\n\n${greet}, this has been flagged at the management level. Please address it today and reply here with the status.\n\n${sign}`;
    }
    return `${greet},\n\nFlagging this for your attention: ${alert.title}. Reply here with what's going on and what you need from me.\n\n${sign}`;
  };

  const start = (m) => { setMode(m); setText(composeText(m)); };
  const cancel = () => { setMode(null); setText(''); };

  const send = async () => {
    if (!text.trim()) { toast.error('Add a message before sending'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/hod/agents/${targetId}/direct-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_text: text, agent_id: targetId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.error || data?.detail || 'Failed to send');
        return;
      }
      toast.success(`${mode === 'warning' ? 'Warning' : 'Feedback'} sent to ${personName}`);
      cancel();
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  if (!mode) {
    return (
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-current/10">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => start('feedback')} disabled={!token}>
          <MessageSquare size={12} className="mr-1" /> Feedback
        </Button>
        <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-500 text-white" onClick={() => start('warning')} disabled={!token}>
          <AlertTriangle size={12} className="mr-1" /> Warning
        </Button>
        <span className={`text-xs ml-auto ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          → {personName}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 pt-2 border-t border-current/10 space-y-2">
      <div className="flex items-center justify-between">
        <p className={`text-xs font-semibold ${mode === 'warning' ? 'text-red-600' : 'text-emerald-600'}`}>
          {mode === 'warning' ? 'Sending WARNING' : 'Sending feedback'} to {personName} ({recipientWord})
        </p>
        <button
          type="button"
          onClick={cancel}
          className={`text-xs underline ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
          disabled={sending}
        >
          Cancel
        </button>
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="text-xs" />
      <div className="flex justify-end">
        <Button
          size="sm"
          className={`h-7 text-xs ${mode === 'warning' ? 'bg-red-600 hover:bg-red-500' : 'bg-violet-600 hover:bg-violet-700'} text-white`}
          onClick={send}
          disabled={sending || !text.trim()}
        >
          {sending
            ? <><RefreshCw size={12} className="mr-1 animate-spin" /> Sending</>
            : <><Send size={12} className="mr-1" /> Send {mode === 'warning' ? 'Warning' : 'Feedback'}</>}
        </Button>
      </div>
    </div>
  );
};

const AlertsFlagsPanel = ({ alerts, loading, token }) => {
  const { isDarkMode } = useTheme();

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'high':
      case 'critical':
        return isDarkMode ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
      case 'warning':
        return isDarkMode ? 'bg-amber-900/20 border-amber-700 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800';
      default:
        return isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`p-3 rounded-lg ${isDarkMode ? 'bg-muted' : 'bg-slate-100'} animate-pulse h-16`} />
        ))}
      </div>
    );
  }

  if (!alerts?.length) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        <CheckCircle2 size={40} className="mx-auto mb-2 text-emerald-500" />
        <p>No alerts - All clear!</p>
      </div>
    );
  }

  const fmtTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const ago = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return null;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const sinceLabel = (alert) => {
    switch (alert.type) {
      case 'overdue_followup': return 'Due';
      case 'stuck_deal': return 'Last update';
      case 'ooo_no_return': return 'Out since';
      case 'no_status_update': return alert.last_activity_at ? 'Last activity' : 'No activity since';
      default: return 'Since';
    }
  };

  return (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {alerts.map((alert, idx) => {
          const raisedStr = fmtTime(alert.raised_at);
          const sinceStr = fmtTime(alert.since);
          const sinceAgo = ago(alert.since);
          return (
            <div
              key={idx}
              className={`p-3 rounded-lg border ${getSeverityStyle(alert.severity)}`}
              data-testid={`alert-${idx}`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{alert.title}</p>
                  {(sinceStr || raisedStr) && (
                    <p className={`text-xs mt-1 flex items-center gap-1 font-mono ${isDarkMode ? 'opacity-70' : 'opacity-80'}`}>
                      <Clock size={11} />
                      {sinceStr ? (
                        <>
                          <span>{sinceLabel(alert)}: {sinceStr}</span>
                          {sinceAgo && <span className="opacity-70">({sinceAgo})</span>}
                        </>
                      ) : (
                        <span>Flagged at {raisedStr}</span>
                      )}
                    </p>
                  )}
                  {alert.assigned_to && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'opacity-70' : 'opacity-80'}`}>
                      Assigned to: {alert.assigned_to}
                    </p>
                  )}
                </div>
                <Badge variant="outline" className="text-xs flex-shrink-0">
                  {alert.type?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <AlertAgentActions alert={alert} token={token} isDarkMode={isDarkMode} />
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export default AlertsFlagsPanel;
